import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "./SubscriptionModal.module.css";

const SubscriptionModal = ({ isOpen, onClose, currentUser, onSubscriptionUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [upgradePlans, setUpgradePlans] = useState([]);
  const [downgradePlans, setDowngradePlans] = useState([]);
  const [activeTab, setActiveTab] = useState("upgrade"); // "upgrade" or "downgrade"
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPriceOption, setSelectedPriceOption] = useState(null); // For monthly/yearly selection
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [priceIntervalFilter, setPriceIntervalFilter] = useState("month"); // "month" or "year"

  useEffect(() => {
    if (isOpen) {
      fetchUpgradeDowngradeOptions();
    }
  }, [isOpen]);

  const fetchUpgradeDowngradeOptions = async () => {
    try {
      setLoading(true);
      const res = await authFetch(
        `${BASE_URL}/subscription/upgrade-downgrade-options`
      );
      const data = await res.json();

      console.log("📦 Subscription options data:", data);

      if (res.ok) {
        setCurrentPlan(data.currentPlan);
        setCurrentSubscription(data.currentSubscription);
        setUpgradePlans(data.upgradePlans || []);
        setDowngradePlans(data.downgradePlans || []);

        // Set price interval filter based on current subscription
        // If yearly subscriber, lock to yearly only. If monthly, default to monthly.
        const currentInterval = data.currentSubscription?.interval || "month";
        setPriceIntervalFilter(currentInterval);

        console.log("✅ Current Plan:", data.currentPlan?.name);
        console.log("   - Current Interval:", currentInterval);
        console.log("   - Has prices:", data.currentPlan?.prices?.length || 0);
        console.log("⬆️ Upgrade Plans:", data.upgradePlans?.length || 0);
        console.log("⬇️ Downgrade Plans:", data.downgradePlans?.length || 0);
        
        // Log price details for debugging
        if (data.upgradePlans && data.upgradePlans.length > 0) {
          console.log("📈 Upgrade Plans with Prices:");
          data.upgradePlans.forEach(plan => {
            console.log(`   - ${plan.name}:`, 
              plan.prices?.map(p => `${p.interval} (${p.stripePriceId})`) || ['no prices']);
          });
        }
        
        if (data.downgradePlans && data.downgradePlans.length > 0) {
          console.log("📉 Downgrade Plans with Prices:");
          data.downgradePlans.forEach(plan => {
            console.log(`   - ${plan.name}:`,
              plan.prices?.map(p => `${p.interval} (${p.stripePriceId})`) || ['no prices']);
          });
        }
        
        // Set default tab to upgrade only (downgrade is disabled)
        if (data.upgradePlans && data.upgradePlans.length > 0) {
          setActiveTab("upgrade");
        }
      } else {
        toast.error(data.message || "Failed to load subscription options");
      }
    } catch (error) {
      console.error("Error fetching subscription options:", error);
      toast.error("Failed to load subscription options");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpgradeDowngrade = async () => {
    if (!selectedPlan) return;

    console.log("🚀 Initiating subscription change:");
    console.log("  - Selected Plan:", selectedPlan.name);
    console.log("  - Plan ID:", selectedPlan.id);
    console.log("  - Price Option:", selectedPriceOption);
    console.log("  - Interval:", priceIntervalFilter);

    try {
      setProcessing(true);
      
      // Use the upgrade endpoint for both upgrades and downgrades
      const res = await authFetch(`${BASE_URL}/subscription/upgrade`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newPlanId: selectedPlan.id,
          priceId: selectedPriceOption?.id || null,
          stripePriceId: selectedPriceOption?.stripePriceId || null,
          interval: priceIntervalFilter,
        }),
      });

      console.log("📡 Request sent with data:", {
        newPlanId: selectedPlan.id,
        priceId: selectedPriceOption?.id,
        stripePriceId: selectedPriceOption?.stripePriceId,
        interval: priceIntervalFilter,
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(
          t("Subscription upgraded successfully!") || "Subscription upgraded successfully!"
        );
        
        // Call the callback to refresh user data
        if (onSubscriptionUpdate) {
          await onSubscriptionUpdate();
        }
        
        // Close modal
        onClose();
      } else {
        // Handle PayPal approval redirect
        if (data.requiresUserAction && data.approvalUrl) {
          toast.info("Redirecting to PayPal for approval...");
          window.location.href = data.approvalUrl;
        } else {
          toast.error(data.message || "Failed to process subscription change");
        }
      }
    } catch (error) {
      console.error("Error processing subscription change:", error);
      toast.error("An error occurred while processing your request");
    } finally {
      setProcessing(false);
      setShowConfirmation(false);
    }
  };

  const formatCurrency = (amount, currency = "CHF") => {
    if (!amount) return "N/A";
    const numberValue = parseFloat(amount);

    // Swiss CHF format with apostrophe as thousand separator
    const formatted = numberValue.toFixed(2);
    const parts = formatted.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    return `${integerPart}.${parts[1]} ${currency}`;
  };

  const formatInterval = (interval) => {
    if (!interval) return "";
    return interval === "month" ? t("monthly") || "/ month" : t("yearly") || "/ year";
  };

  const getPriceForInterval = (plan, interval) => {
    // If plan has prices array, find the matching interval
    if (plan.prices && plan.prices.length > 0) {
      const priceOption = plan.prices.find(p => p.interval === interval && p.isActive);
      if (priceOption) {
        return {
          amount: priceOption.amount,
          currency: priceOption.currency || plan.currency,
          id: priceOption.id,
          stripePriceId: priceOption.stripePriceId,
          matchingCount: priceOption.matchingCount || plan.matchingCount
        };
      }
    }
    // Fallback to plan's default price
    return {
      amount: plan.price,
      currency: plan.currency,
      id: null,
      stripePriceId: null,
      matchingCount: plan.matchingCount
    };
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2>{t("Manage Subscription") || "Manage Subscription"}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>{t("Loading subscription options...") || "Loading..."}</p>
          </div>
        ) : (
          <>
            {/* Current Plan Info */}
            {currentPlan ? (
              <div className={styles.currentPlanSection}>
                <h3>{t("Your Current Plan") || "Your Current Plan"}</h3>
                <div className={styles.currentPlanCard}>
                  <div className={styles.currentPlanBadge}>
                    {t("ACTIVE") || "ACTIVE"}
                  </div>
                  <div className={styles.planName}>{currentPlan.name}</div>
                  <div className={styles.planPrice}>
                    {(() => {
                      const currentInterval = currentSubscription?.interval || currentPlan.interval;
                      const currentPrice = getPriceForInterval(currentPlan, currentInterval);
                      return (
                        <>
                          {formatCurrency(currentPrice.amount || currentPlan.price, currentPrice.currency || currentPlan.currency)}
                          {" "}
                          <span className={styles.interval}>
                            {formatInterval(currentInterval)}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <div className={styles.planDetails}>
                    <p>
                      <strong>📊 {t("Matching Count") || "Matching Count"}:</strong>{" "}
                      {currentPlan.matchingCount || 0}
                    </p>
                    {currentSubscription?.endDate && (
                      <p>
                        <strong>📅 {t("Renews On") || "Renews On"}:</strong>{" "}
                        {new Date(currentSubscription.endDate).toLocaleDateString()}
                      </p>
                    )}
                    {currentSubscription?.paymentMethod && (
                      <p>
                        <strong>💳 {t("Payment") || "Payment"}:</strong>{" "}
                        {currentSubscription.paymentMethod.charAt(0).toUpperCase() + 
                         currentSubscription.paymentMethod.slice(1)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.noSubscriptionBanner}>
                <div className={styles.bannerIcon}>🎯</div>
                <h3>{t("No Active Subscription") || "No Active Subscription"}</h3>
                <p>{t("Choose a plan below to get started") || "Choose a plan below to get started"}</p>
              </div>
            )}

            {/* Tabs for Upgrade / Downgrade - Only show if user has current plan */}
            {currentPlan && (
              <div className={styles.tabContainer}>
                <button
                  className={`${styles.tab} ${activeTab === "upgrade" ? styles.activeTab : ""}`}
                  onClick={() => setActiveTab("upgrade")}
                  disabled={upgradePlans.length === 0}
                >
                  {t("Upgrade") || "Upgrade"} ({upgradePlans.length})
                </button>
                {/* <button
                  className={`${styles.tab} ${activeTab === "downgrade" ? styles.activeTab : ""}`}
                  onClick={() => setActiveTab("downgrade")}
                  disabled={downgradePlans.length === 0}
                >
                  {t("Downgrade") || "Downgrade"} ({downgradePlans.length})
                </button> */}
              </div>
            )}

            {/* Pricing Interval Toggle - Show only for upgrade plans */}
            {(currentPlan ?
              upgradePlans.length > 0 :
              upgradePlans.length > 0
            ) && (
              <div className={styles.intervalToggle}>
                <button
                  className={`${styles.intervalButton} ${priceIntervalFilter === "month" ? styles.activeInterval : ""}`}
                  onClick={() => setPriceIntervalFilter("month")}
                  disabled={currentSubscription?.interval === "year"}
                  title={currentSubscription?.interval === "year" ? "Cannot downgrade from yearly to monthly billing" : ""}
                >
                  {t("Monthly") || "Monthly"}
                </button>
                <button
                  className={`${styles.intervalButton} ${priceIntervalFilter === "year" ? styles.activeInterval : ""}`}
                  onClick={() => setPriceIntervalFilter("year")}
                >
                  {t("Yearly") || "Yearly"}
                </button>
              </div>
            )}

            {/* Plans List */}
            <div className={styles.plansContainer}>
              {/* Show empty states only when user has a current plan */}
              {currentPlan && upgradePlans.length === 0 && (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>🏆</div>
                  <p>{t("No upgrade options available") || "No upgrade options available"}</p>
                  <p className={styles.emptySubtext}>
                    {t("You are already on the highest plan") || "You are already on the highest plan"}
                  </p>
                </div>
              )}

              {/* Show only upgrade plans (downgrade is disabled) */}
              {(currentPlan
                ? upgradePlans
                : upgradePlans
              ).map((plan) => {
                const priceInfo = getPriceForInterval(plan, priceIntervalFilter);
                
                return (
                  <div key={plan.id} className={styles.planCard}>
                    <div className={styles.planHeader}>
                      <h4>{plan.name}</h4>
                      <div className={styles.planPriceTag}>
                        {formatCurrency(priceInfo.amount, priceInfo.currency)}
                        <span className={styles.intervalSmall}>
                          {formatInterval(priceIntervalFilter)}
                        </span>
                      </div>
                    </div>

                    <div className={styles.planDescription}>
                      <p>{plan.description || t("Premium features included")}</p>
                    </div>

                    <div className={styles.planFeatures}>
                      <div className={styles.feature}>
                        <span className={styles.featureIcon}>✓</span>
                        <span>
                          {priceInfo.matchingCount || plan.matchingCount || 0}{" "}
                          {t("active matchings") || "active matchings"}
                        </span>
                      </div>
                      {plan.prices && plan.prices.length > 1 && (
                        <div className={styles.feature}>
                          <span className={styles.featureIcon}>✓</span>
                          <span>
                            {t("Multiple billing options") || "Multiple billing options"}
                          </span>
                        </div>
                      )}
                    </div>

                    <button
                      className={styles.selectButton}
                      onClick={() => {
                        setSelectedPlan(plan);
                        setSelectedPriceOption(priceInfo);
                        setShowConfirmation(true);
                      }}
                      disabled={processing}
                    >
                      {t("Upgrade to this plan") || "Upgrade to this plan"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Confirmation Dialog */}
        {showConfirmation && selectedPlan && (
          <div className={styles.confirmationOverlay}>
            <div className={styles.confirmationDialog}>
              <h3>
                {t("Confirm Upgrade") || "Confirm Upgrade"}
              </h3>
              <p>
                {t("Are you sure you want to upgrade to") ||
                  "Are you sure you want to upgrade to"}{" "}
                <strong>{selectedPlan.name}</strong>?
              </p>
              <p className={styles.confirmPrice}>
                {selectedPriceOption 
                  ? formatCurrency(selectedPriceOption.amount, selectedPriceOption.currency)
                  : formatCurrency(selectedPlan.price, selectedPlan.currency)}{" "}
                {formatInterval(priceIntervalFilter)}
              </p>
              {currentSubscription?.paymentMethod === "stripe" && (
                <p className={styles.prorationNote}>
                  {t("You will be charged the prorated amount immediately.") ||
                    "You will be charged the prorated amount immediately."}
                </p>
              )}
              <div className={styles.confirmActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowConfirmation(false);
                    setSelectedPlan(null);
                  }}
                  disabled={processing}
                >
                  {t("Cancel") || "Cancel"}
                </button>
                <button
                  className={styles.confirmButton}
                  onClick={handleConfirmUpgradeDowngrade}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <span className={styles.buttonSpinner}></span>
                      {t("Processing...") || "Processing..."}
                    </>
                  ) : (
                    t("Confirm") || "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionModal;
